import { Archive, ArchiveRestore, ArrowLeft, MoreHorizontal, Pencil } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { MediaRefImage } from '@/components/media-ref-image';
import { ProposalDialog, type ProposalValues } from '@/components/proposal-dialog';
import { RichText } from '@/components/rich-text';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useProposal } from '@/lib/api-hooks';
import { proposalStatusStyles } from '@/lib/proposal-status';
import { archiveProposal, restoreProposal, updateProposal } from '@/lib/proposals';
import { invalidateResource } from '@/lib/resource';
import { cn } from '@/lib/utils';

export function ProposalDetailPage() {
  const { slug } = useParams();
  const { data: proposal, isLoading } = useProposal(slug);
  const [editOpen, setEditOpen] = useState(false);

  async function handleEdit(values: ProposalValues) {
    if (!proposal) return;
    await updateProposal(proposal.id, values);
    invalidateResource('proposal');
    invalidateResource('proposals');
  }

  async function handleArchive() {
    if (!proposal) return;
    await archiveProposal(proposal.id);
    invalidateResource('proposal');
  }

  async function handleRestore() {
    if (!proposal) return;
    await restoreProposal(proposal.id);
    invalidateResource('proposal');
  }

  if (isLoading) {
    return <p className="text-muted-foreground text-sm">Loading proposal…</p>;
  }

  if (!proposal) {
    return (
      <div className="flex flex-col items-start gap-4">
        <Link
          to="/proposals"
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to proposals
        </Link>
        <p className="text-muted-foreground text-sm">Proposal not found.</p>
      </div>
    );
  }

  const stats = [
    { label: 'Votes', value: proposal.votes.toLocaleString() },
    { label: 'Artist', value: proposal.artist },
    { label: 'Project', value: proposal.project },
    { label: 'Updated', value: proposal.updated },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <Link
          to="/proposals"
          className="inline-flex w-fit items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to proposals
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="size-4" />
            Edit
          </Button>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger
              aria-label="Proposal actions"
              className="inline-flex size-9 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <MoreHorizontal className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {proposal.archived ? (
                <DropdownMenuItem onSelect={handleRestore}>
                  <ArchiveRestore />
                  Restore
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={handleArchive}>
                  <Archive />
                  Archive
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-semibold text-2xl tracking-tight">{proposal.title}</h1>
          <Badge variant="outline" className={cn(proposalStatusStyles[proposal.status])}>
            {proposal.status}
          </Badge>
        </div>
        <RichText html={proposal.description} className="max-w-2xl text-muted-foreground" />
        <p className="text-muted-foreground text-sm">
          Part of{' '}
          <Link
            to={`/projects/${proposal.projectSlug}`}
            className="font-medium text-foreground hover:underline"
          >
            {proposal.project}
          </Link>{' '}
          by{' '}
          <Link
            to={`/artists/${proposal.artistSlug}`}
            className="font-medium text-foreground hover:underline"
          >
            {proposal.artist}
          </Link>
          .
        </p>
      </div>

      {(proposal.coverMedia || (proposal.gallery?.length ?? 0) > 0) && (
        <div className="flex flex-col gap-4">
          {proposal.coverMedia && (
            <MediaRefImage
              media={proposal.coverMedia}
              className="aspect-video w-full max-w-3xl rounded-lg border"
            />
          )}
          {(proposal.gallery?.length ?? 0) > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {proposal.gallery?.map((media) => (
                <MediaRefImage
                  key={media.id}
                  media={media}
                  className="aspect-video w-full rounded-md border"
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 border-y py-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="space-y-1">
            <p className="text-muted-foreground text-sm">{stat.label}</p>
            <p className="font-medium text-base">{stat.value}</p>
          </div>
        ))}
      </div>

      {editOpen && (
        <ProposalDialog
          open
          onOpenChange={setEditOpen}
          mode="edit"
          initialValues={{
            title: proposal.title,
            projectId: proposal.projectId,
            project: proposal.project,
            artistId: proposal.artistId,
            artist: proposal.artist,
            status: proposal.status,
            description: proposal.description,
            coverMedia: proposal.coverMedia ?? null,
            gallery: proposal.gallery ?? [],
          }}
          onSubmit={handleEdit}
        />
      )}
    </div>
  );
}
